import uniqBy from 'lodash/uniqBy'
import { SUBMIT_BID_ACTION_TYPES, getBids, getBidsCount } from 'utils/actions/bids'
import { closeDialog, UI_ACTION_TYPES } from 'utils/actions/ui'
import { callApi, ENDPOINTS } from 'utils/api/callApi'
import { EXTERNAL_ENTITY } from 'constants/entities'
import {
  clearSelectedCarrier,
  selectNewCarrier,
  validateCarrier as triggerValidateCarrier,
  SEARCH_SAFER_ACTION_TYPES,
  checkCompliance,
} from '../actions/options'
import { SIDEBAR_ACTION_TYPES } from '../actions/sidebar'

// when we find SAFER records, we attach matching records
export const matchSaferRecords = store => next => async action => {
  if (action.type !== SEARCH_SAFER_ACTION_TYPES.SAFER_SUCCESS) return next(action)
  const actionWith = data => {
    const finalAction = { ...action, ...data }
    return finalAction
  }
  try {
    const { dot, mc, requestData } = action.payload
    const listCarrierCalls = []
    const listCarrierCallOrigins = []

    // helper method that adds a search for a particular way we might mismatch MC/DOT
    const addToCarrierCalls = (value, fieldName, original) => {
      const trimmed = `${value}`.trim()
      listCarrierCallOrigins.push({ ...original })
      listCarrierCalls.push(
        callApi({
          endpoint: ENDPOINTS.search,
          params: {
            filters: {
              search_options: [
                {
                  type: 'carrier',
                  [fieldName]: trimmed,
                },
              ],
              search_term: `*${trimmed}*`,
            },
          },
        })
      )
    }
    const originalQuery = requestData.params.query
    let candidates = []

    if (dot && (dot.mcNumber || dot.dotNumber)) {
      if (dot.dotNumber) {
        addToCarrierCalls(dot.dotNumber, 'dot_number', dot)
        addToCarrierCalls(dot.dotNumber, 'mc_number', dot)
      }
      if (dot.mcNumber) {
        addToCarrierCalls(dot.mcNumber, 'mc_number', dot)
      }
    }

    if (mc && (mc.mcNumber || mc.dotNumber)) {
      if (mc.mcNumber) {
        addToCarrierCalls(mc.mcNumber, 'mc_number', mc)
        addToCarrierCalls(mc.mcNumber, 'dot_number', mc)
      }
      if (mc.dotNumber) {
        addToCarrierCalls(mc.dotNumber, 'dot_number', mc)
      }
    }

    const carrierMatchResults = await Promise.all(listCarrierCalls)
    candidates = candidates.concat(
      ...carrierMatchResults.map((item, index) => {
        const newList = item.namesearch.map(subItem => ({
          ...subItem,
          original: listCarrierCallOrigins[index],
        }))
        return newList
      })
    )

    candidates = uniqBy(candidates, 'id')
    candidates = candidates.filter(item => item.mcNumber || item.dotNumber)

    candidates.push({
      id: 'new-mc',
      isNewRecord: true,
      name: `Create new carrier with MC ${originalQuery}`,
      mcNumber: originalQuery,
    })
    candidates.push({
      id: 'new-dot',
      isNewRecord: true,
      name: `Create new carrier with DOT ${originalQuery}`,
      dotNumber: originalQuery,
    })
    const firstCandidate = candidates[0]
    const combinedResults = { ...action.payload, candidates }
    if (!firstCandidate.isNewRecord) {
      const { dotNumber, mcNumber } = firstCandidate.original
      const carrierUpdates = { id: candidates[0].id }
      if (mcNumber) carrierUpdates.updateMc = `${mcNumber}`.trim()
      if (dotNumber) carrierUpdates.dotNumber = `${dotNumber}`.trim()
      store.dispatch(triggerValidateCarrier(carrierUpdates))
    } else {
      store.dispatch(selectNewCarrier(candidates[0]))
    }

    return next(
      actionWith({
        type: SEARCH_SAFER_ACTION_TYPES.SAFER_SUCCESS,
        payload: combinedResults,
      })
    )
  } catch (error) {
    return next(actionWith({ type: SEARCH_SAFER_ACTION_TYPES.SAFER_FAILURE }))
  }
}

export const validateCarrier = store => next => async action => {
  const { payload = {} } = action
  if (!action.validateCarrier) return next(action)

  const currentState = store.getState()
  const actionWith = data => {
    const finalAction = { ...action, ...data }
    delete finalAction[payload]
    return finalAction
  }

  const [requestType, successType, failureType] = payload.actionTypes

  const { id } = payload
  //  why were we doing this?
  if (!id || id !== EXTERNAL_ENTITY.id) {
    next(actionWith({ type: requestType, payload }))
  }

  const {
    forms: {
      optionForm: {
        carrier: { pristine },
      },
    },
    options: { carrierData },
    sidebar: { activeRowId: shipmentId },
  } = currentState
  if (pristine || !id) return next(action)
  if (id && id === carrierData.id) return next(action)
  if (id && id === EXTERNAL_ENTITY.id) return next(action)
  try {
    const carrierRequest = { endpoint: ENDPOINTS.carriers, id }
    const { carrierApplication } = await callApi(carrierRequest)
    const { location: { contactPhone, contactPhoneExt } = {} } = carrierApplication

    store.dispatch(checkCompliance({ shipmentId, carrierId: id }))
    return next(
      actionWith({
        type: successType,
        payload: { carrierApplication },
      })
    )
  } catch (error) {
    return next(actionWith({ type: failureType }))
  }
}

export const handleResetForm = store => next => async action => {
  const { CLOSE_DIALOG } = UI_ACTION_TYPES
  const { SIDEBAR_CLOSED } = SIDEBAR_ACTION_TYPES
  if ([SIDEBAR_CLOSED, CLOSE_DIALOG, 'rrf/change'].includes(action.type)) {
    if (action.type === 'rrf/change' && (action.value || action.model !== 'optionForm.carrier'))
      return next(action)
    store.dispatch(clearSelectedCarrier())
    return next(action)
  }
  return next(action)
}

const { SUBMIT_BID_SUCCESS } = SUBMIT_BID_ACTION_TYPES

export const handleOptionsPostSuccess = store => next => action => {
  if (action.type !== SUBMIT_BID_SUCCESS) return next(action)

  const currentState = store.getState()
  const {
    shipments: { shipmentList },
    sidebar: { activeRowId },
  } = currentState
  const ids = shipmentList.map(s => s.id)
  store.dispatch(closeDialog())
  store.dispatch(getBids(activeRowId))
  store.dispatch(getBidsCount(ids))
  return next(action)
}
